import { Star, Award, Quote } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";
import { motion } from "framer-motion";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/v2/ui/avatar";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/v2/ui/carousel";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import MandalaWatermark from "./MandalaWatermark";

const priest1 = "/images/priest-1.jpg";
const priest2 = "/images/priest-1.jpg";
const priest3 = "/images/priest-1.jpg";
const priest4 = "/images/priest-1.jpg";
const priest5 = "/images/priest-1.jpg";
const priest6 = "/images/priest-1.jpg";

const priests = [
  { name: "Pt. Gangasagar Pandey", initials: "GP", image: priest1, languages: ["Hindi", "Sanskrit"], city: "Bangalore", years: 20, pujas: 500, rating: 4.9, featured: true, quote: "Consistent bookings keep my schedule full year-round" },
  { name: "Pt. Guru Datta", initials: "GD", image: priest2, languages: ["Kannada", "Telugu"], city: "Bangalore", years: 15, pujas: 700, rating: 4.8, featured: false, quote: "Fair and timely payments after every booking" },
  { name: "Pt. Rajesh Vadhiyar", initials: "RV", image: priest3, languages: ["Tamil", "Sanskrit"], city: "Chennai", years: 10, pujas: 500, rating: 4.9, featured: true, quote: "The seva team handles all coordination — I just focus on the puja" },
  { name: "Pt. Venkat Sharma", initials: "VS", image: priest4, languages: ["Telugu", "Sanskrit"], city: "Hyderabad", years: 12, pujas: 350, rating: 4.8, featured: false, quote: "They provide quality samagri so I don't have to worry about materials" },
  { name: "Pt. Aniket Kulkarni", initials: "AK", image: priest5, languages: ["Marathi", "Sanskrit"], city: "Pune", years: 11, pujas: 380, rating: 4.8, featured: false, quote: "Professional platform that respects our traditions and expertise" },
  { name: "Pt. Harish Trivedi", initials: "HT", image: priest6, languages: ["Kannada", "Sanskrit"], city: "Bangalore", years: 8, pujas: 200, rating: 4.8, featured: false, quote: "Families are well-prepared thanks to BookMyPooja's guidance" },
];

const FeaturedPriestsSection = ({ priestsData }) => {
  const displayPriests = priestsData?.priests || priests;

  return (
    <section className="py-8 md:py-12 bg-background relative overflow-hidden">
      <MandalaWatermark className="absolute -right-32 top-0 w-[400px] h-[400px] text-primary opacity-[0.03] pointer-events-none" />

      <div className="container relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-6"
        >
          <h2 className="text-2xl md:text-3xl mb-2">
            {priestsData?.title?.split(priestsData?.highlight)[0]} <span className="text-primary">{priestsData?.highlight}</span> {priestsData?.title?.split(priestsData?.highlight)[1]}
          </h2>
          <p className="text-sm text-muted-foreground font-sans max-w-lg mx-auto">
            {priestsData?.subtitle}
          </p>
        </motion.div>

        <Carousel opts={{ align: "start", loop: true, dragFree: true }} plugins={[WheelGesturesPlugin()]} className="mx-auto max-w-5xl">
          <CarouselContent className="-ml-3">
            {displayPriests.map((priest, i) => (
              <CarouselItem key={priest.name} className="pl-3 basis-[70%] sm:basis-[45%] lg:basis-1/3">
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="relative p-5 rounded-xl border border-border bg-card flex flex-col justify-between min-h-[240px]"
                >
                  {priest.featured && (
                    <div className="flex items-center gap-1 bg-gold/15 text-gold border border-gold/30 rounded-full px-2 py-0.5 text-[10px] font-semibold font-sans whitespace-nowrap w-fit mb-3">
                      <Award className="h-3 w-3" />
                      Pandit of the Month
                    </div>
                  )}

                  {/* Quote */}
                  <div className="flex-1 mb-4">
                    <Quote className="h-5 w-5 text-primary/30 mb-2" />
                    <p className="text-sm italic text-foreground/85 leading-relaxed">
                      "{priest.quote}"
                    </p>
                  </div>

                  {/* Priest info */}
                  <div className="flex items-center gap-3 pt-3 border-t border-border/50">
                    <Avatar className="h-10 w-10 border border-gold/40 flex-shrink-0">
                      <AvatarImage src={priest.image} alt={`Verified Pandit ${priest.name} in ${priest.city}`} loading="lazy" />
                      <AvatarFallback className="bg-primary/10 text-primary font-semibold text-sm">
                        {priest.initials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-sm truncate">{priest.name}</h3>
                      <p className="text-[11px] text-muted-foreground font-sans">
                        {priest.city} · {priest.years}+ yrs · {priest.pujas}+ pujas
                      </p>
                      <div className="flex items-center gap-1 mt-0.5">
                        <Star className="h-3 w-3 fill-gold text-gold" />
                        <span className="text-[11px] font-semibold font-sans">{priest.rating}</span>
                        <span className="text-[10px] text-muted-foreground font-sans ml-1">
                          {priest.languages.join(", ")}
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="hidden md:flex -left-10" />
          <CarouselNext className="hidden md:flex -right-10" />
        </Carousel>

        {/* Bottom text with FAQ link */}
        <div className="text-center mt-5">
          <p className="text-xs text-muted-foreground font-sans max-w-lg mx-auto leading-relaxed">
            {priestsData?.footerNote?.split("FAQs")[0]}
            <a
              href="#faq"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection("faq");
              }}
              className="text-primary font-semibold underline underline-offset-2 hover:text-primary/80 transition-colors"
            >
              FAQs
            </a>
            {priestsData?.footerNote?.split("FAQs")[1]}
          </p>
        </div>
      </div>
    </section>
  );
}

export default FeaturedPriestsSection;
